import React, { useEffect, useState } from "react";
import { NextPage } from "next";
import {
    Button,
    HTMLHeader,
    LoadingIndicator,
    Navbar,
    BlogPostCard,
    BlogVisitorPopup,
    SharePopup,
} from "@Modules/common";
import { IHTMLHeader } from "@Modules/common/HTMLHeader/HTMLHeader";
import { IBlogPage } from "@Interfaces";
import {
    createAction,
    API_ACTION_TYPES,
    ACTION_TYPES,
    SELECTORS,
    useFetchNavbarItemData,
    BUTTON_DESIGNS,
    useFetchStatus,
    FETCH_STATUS,
    isEmpty,
    useData,
} from "@Utils";
import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_PROFILE_PIC_SRC } from "repoV2/utils/common/assets/assets";
import { LOADING_INDICATOR_DESIGNS } from "repoV2/components/common/LoadingIndicators/LoadingIndicator.data";
import styles from "./blog.module.scss";

const Blog: NextPage<IBlogPage.IProps, IBlogPage.InitialProps> = ({
    hostName,
}: IBlogPage.IProps) => {
    const [canRender, setCanRender] = useState(false);

    const host = useSelector(SELECTORS.host);
    const hostData = host?.dataHost?.profile_data;

    useFetchNavbarItemData(hostName);

    const dispatch = useDispatch();
    const [page, setPage] = useState(1);
    const blogData = host.blog;
    const fetchBlogPostsStatus = useFetchStatus(
        API_ACTION_TYPES.FETCH_BLOG_POSTS
    );

    const { data: showBlogVisitorPopup, setData: setShowBlogVisitorPopup } =
        useData("showBlogVisitorPopup");
    const closeBlogVisitorPopup = () => {
        setShowBlogVisitorPopup(false);
    };

    const { data: sharePopupData, setData: setSharePopupData } =
        useData("sharePopupData");
    const closeSharePopup = () => {
        setSharePopupData({ show: false, props: {} });
    };

    const fetchBlogPosts = () => {
        dispatch(
            createAction(ACTION_TYPES.UTILS.API_CALL, {
                apiActionType: API_ACTION_TYPES.FETCH_BLOG_POSTS,
                urlArgs: { page: page + 1, hostName },
                successCallback: () => {
                    setPage(page + 1);
                },
            })
        );
    };

    useEffect(() => {
        // Done intentionally to avoid showing empty state on first load.
        // Since these apis are paginated, whenever next api call is made,
        // It will rerender the whole page
        setTimeout(() => {
            setCanRender(true);
        }, 200);
    }, []);

    const onLoadMoreClick = () => {
        fetchBlogPosts();
    };

    const htmlHeaderProps: IHTMLHeader.IProps = {
        title: `${hostData?.name ? `${hostData.name}'s ` : ""}Blog`,
        favicon: hostData?.profile_pic || DEFAULT_PROFILE_PIC_SRC,
    };

    if (!canRender) {
        return (
            <>
                <HTMLHeader {...htmlHeaderProps} />
                <div className={styles.root}>
                    <div className={styles.loaderContainer}>
                        <LoadingIndicator
                            applyTheme
                            design={LOADING_INDICATOR_DESIGNS.rotating_arcs}
                        />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <HTMLHeader {...htmlHeaderProps} />
            <div className={styles.root}>
                <Navbar hostData={hostData} template={host.template} />
                <div className={styles.body}>
                    <h1 className={styles.title}>Blog Post</h1>
                    <div className={styles.blogPostsContainer}>
                        {blogData.data.map(data => (
                            <BlogPostCard data={data} key={data.uuid} />
                        ))}
                        {isEmpty(blogData.data) ? (
                            <div className={styles.emptyState}>
                                No Posts Yet!!
                            </div>
                        ) : null}
                    </div>
                    {blogData.data.length < blogData.total ? (
                        <div className={styles.loadMoreContainer}>
                            <Button
                                design={BUTTON_DESIGNS.ROUNDED_CORNERS}
                                content={
                                    fetchBlogPostsStatus ===
                                    FETCH_STATUS.FETCHING
                                        ? "Loading..."
                                        : "Load more"
                                }
                                disabled={
                                    fetchBlogPostsStatus ===
                                    FETCH_STATUS.FETCHING
                                }
                                onClick={onLoadMoreClick}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
            {showBlogVisitorPopup ? (
                <BlogVisitorPopup closePopup={closeBlogVisitorPopup} />
            ) : null}
            {sharePopupData?.show ? (
                <SharePopup
                    closePopup={closeSharePopup}
                    {...sharePopupData.props}
                />
            ) : null}
        </>
    );
};

Blog.getInitialProps = async (
    ctx: IBlogPage.IPageContext
): Promise<IBlogPage.InitialProps> => {
    const { hostName } = ctx.params;

    ctx.store.dispatch(
        createAction(ACTION_TYPES.UTILS.API_CALL, {
            apiActionType: API_ACTION_TYPES.FETCH_HOST,
            urlArgs: { hostName },
        })
    );
    ctx.store.dispatch(
        createAction(ACTION_TYPES.UTILS.API_CALL, {
            apiActionType: API_ACTION_TYPES.FETCH_HOST_THEME,
            urlArgs: { hostName },
        })
    );

    return { hostName };
};

export default Blog;
